// Request util for api call
import request from '~/utils/request';
import { appendSortParams } from '~/utils/sort-params';

// Actions Types
import {
  SET_ORDERS_DATA,
  SET_ORDERS_LOADER,
  SET_ORDERS_FILTERS_LABELS,
  SET_ORDERS_DETAIL_CONTACTS_DATA,
  SET_ORDERS_DETAIL_OVERVIEW_DETAIL,
  SET_ORDER_DETAIL_LOADER,
  CONTACTS_SEARCH,
  SET_ORDER_DETAIL_CONTACTS_LOADER,
  SET_ORDERS_CAMPAIGNS_DETAIL_DETAIL,
  SET_ORDER_BATCHING_DETAIL,
} from './action-types';

// Utiils
import { dateFormat } from '~/utils/date-format';
import { DEFAULT_PAGINATION } from '~/utils/constants';
import { formatCount } from '~/utils/helperFunctions';
import { failure } from '~/redux/actions/snackbar-actions';
let isFetching = false;

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.values(value).some(hasValue);
  return value !== null && value !== undefined && value !== '';
};

const getFindNewContactsListType = (config = {}, listName = '') => {
  if (config.findNewMode === 'addresses') return 'Radius List';
  if (config.findNewMode === 'locations') return 'Area List';

  const searchMethod = String(config.searchMethod || '').toLowerCase();
  if (searchMethod.includes('radius') || searchMethod.includes('address')) return 'Radius List';
  if (searchMethod.includes('area') || searchMethod.includes('location')) return 'Area List';

  if (
    config.locationFilter ||
    (Array.isArray(config.customShapes) && config.customShapes.length > 0) ||
    (Array.isArray(config.drawRadiusAreas) && config.drawRadiusAreas.length > 0)
  ) {
    return 'Area List';
  }
  if (Array.isArray(config.addressConfigs) && config.addressConfigs.length > 0) {
    return 'Radius List';
  }

  if (/^\s*radius list\b/i.test(listName)) return 'Radius List';
  if (/^\s*area list\b/i.test(listName)) return 'Area List';

  return null;
};

const getFindNewContactsPurchaseDetails = (order) => {
  const importLog = (order.contactImportLogs || []).find(
    (log) => log?.meta?.source === 'find_new_contacts_data_axle'
  );
  const orderMeta = order.meta || {};
  const isFindNewContactsPurchase = Boolean(
    importLog ||
    orderMeta.source === 'find_new_contacts_data_axle' ||
    orderMeta.findNewContactsPurchase
  );
  if (!isFindNewContactsPurchase) return null;

  const meta = importLog?.meta || orderMeta;
  const config =
    meta.request?.config || orderMeta.request?.config || meta.config || orderMeta.config || {};
  const purchase = meta.purchase || orderMeta.purchase || {};
  const addressCount = Array.isArray(config.addressConfigs) ? config.addressConfigs.length : 0;
  const radiusCount = Array.isArray(config.drawRadiusAreas) ? config.drawRadiusAreas.length : 0;
  const polygonCount = Array.isArray(config.customShapes) ? config.customShapes.length : 0;
  const areaParts = [
    addressCount ? `${formatCount(addressCount)} address${addressCount === 1 ? '' : 'es'}` : null,
    radiusCount ? `${formatCount(radiusCount)} radius area${radiusCount === 1 ? '' : 's'}` : null,
    polygonCount ? `${formatCount(polygonCount)} drawn area${polygonCount === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  const filterCount = Object.values(config.filters || {}).filter(hasValue).length;
  const targetCount =
    purchase.quantity || meta.targetCount || importLog?.quantity || order.quantity;
  const imported = meta.imported || importLog?.inFileDuplicationResult?.imported;
  const listName =
    importLog?.fileName ||
    meta.listName ||
    purchase.listName ||
    order.importListName ||
    order.name ||
    'Find New Contacts';
  const listType = getFindNewContactsListType(config, listName);
  const productType = ['Find New Contacts', listType].filter(Boolean).join(' ');

  return {
    label: `Find New Contacts (${formatCount(targetCount)})`,
    listType,
    productType,
    quantity: targetCount,
    tooltip: [
      `List: ${listName}`,
      `Purchased: ${formatCount(targetCount)} records`,
      imported !== undefined ? `Imported: ${formatCount(imported)} records` : null,
      areaParts.length ? `Area: ${areaParts.join(', ')}` : null,
      filterCount ? `Filters: ${filterCount} applied` : 'Filters: none',
      config.nearbyTargetMode ? `Mode: ${config.nearbyTargetMode}` : null,
      meta.importStatus ? `Import status: ${meta.importStatus}` : null,
      meta.error ? `Import error: ${meta.error}` : null,
      purchase.purchaseId
        ? `Purchase ID: ${purchase.purchaseId}`
        : meta.purchaseId
          ? `Purchase ID: ${meta.purchaseId}`
          : null,
    ]
      .filter(Boolean)
      .join('\n'),
  };
};

const fetchOrders = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_ORDERS_LOADER, payload: true });

    let url = '/orders';

    let queryPayload = {
      page: payload?.pagination?.page ? payload.pagination.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pagination?.pageSize
        ? payload.pagination.pageSize
        : DEFAULT_PAGINATION.pageSize,
    };

    if (payload?.search?.length) {
      queryPayload.search = payload.search;
    }

    appendSortParams(queryPayload, payload);

    if (payload?.applyFilters) {
      Object.entries(payload.filters).forEach(([key, value]) => {
        if (key === 'createdDate' || key === 'mailingDate' || key === 'mailedDate') {
          // If the value is an object, iterate over its properties
          if (value) {
            let date = Object?.entries(value)
              .map(
                ([nestedKey, nestedValue]) =>
                  `${JSON.stringify(nestedKey)}:${JSON.stringify(nestedValue)}`
              )
              .join(', ');
            queryPayload[key] = `{${date}}`;
          }
        } else if (key === 'metaData') {
          for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
              queryPayload[`meta.${key}`] = value[key];
            }
          }
        } else if (key === 'campaigns' || key === 'selectedTemplates') {
          return;
        } else if (key === 'automationId') {
          queryPayload[key] = value;
        } else if (value?.length) {
          queryPayload[key] = `${JSON.stringify(value)}`;
        }
      });
    }

    const response = await request.get(url, queryPayload);

    if (response.status === 200) {
      const combinedData = response.data.data.rows.map((order) => {
        const findNewContactsPurchase = getFindNewContactsPurchaseDetails(order);

        return {
          findNewContactsPurchase,
          id: order.id || ' -',
          date: dateFormat(order.createdAt, true) || ' -',
          status: order.status || ' -',
          source: order.source || ' -',
          isFindNewContactsPurchaseOnly: order.isFindNewContactsPurchaseOnly || false,
          productType:
            findNewContactsPurchase
              ? 'Find New Contacts'
              : order.product?.productType || order.productType || ' -',
          quantity: order.quantity || ' -',
          deliverableQuantity: order.deliverableQuantity || ' -',
          cost: order.cost || 0,
          scheduledDate: dateFormat(order.deliveryDate, true, true) || ' -',
          scheduledMailingDate: order?.mailingDate,
          mailingDate: dateFormat(order.mailingDate, true, true) || ' -',
          rawMailingDate: order.mailingDate || null,
          paymentStatus: order.paymentStatus || ' -',
          checked: false,
          createdBy: order?.creator?.fullName || ' -',
          createdAt: dateFormat(order.createdAt, true) || ' -',
          isCancellable: order.isCancellable || false,
          name: order.name || ' -',
          productId: order.productId,
          product: order.product || {},
          meta: order.meta,
          templateType: order.templateType,
        };
      });
      const orders = {
        rows: combinedData,
        count: response.data.data.count,
        totalRecordsInDB: response.data.data.totalRecordsInDB,
        to: response.data.data.to,
        from: response.data.data.from,
        currentPage: response.data.data.currentPage,
        lastPage: response.data.data.lastPage,
        perPage: response.data.data.perPage,
      };
      dispatch({ type: SET_ORDERS_DATA, payload: orders });
    }
  } catch (error) {
    console.log(error);
    return error;
  } finally {
    dispatch({ type: SET_ORDERS_LOADER, payload: false });
  }
};

const fetchFiltersLabels = () => async (dispatch) => {
  try {
    const labels = await request.get('orders/filters-data');
    let modifiedLabels = {};
    Object.keys(labels.data.data).forEach((key) => {
      const filters = Object.values(labels.data.data[key]).map((item, id) => ({
        id,
        title: item,
      }));
      modifiedLabels[key] = filters;
    });
    dispatch({ type: SET_ORDERS_FILTERS_LABELS, payload: modifiedLabels });
  } catch (error) {
    return error;
  }
};

const fetchOrderCost = (payload) => async () => {
  try {
    const cost = await request.post('orders/calculate-cost', payload);
    return cost;
  } catch (error) {
    return error.response;
  }
};

const creatOrder = (payload) => async () => {
  try {
    const order = await request.post('orders', payload);
    return order;
  } catch (error) {
    return error.response;
  }
};

const fetchOrderContactDetails = (payload) => async (dispatch) => {
  try {
    if (isFetching) {
      return;
    }

    isFetching = true;

    dispatch({ type: SET_ORDER_DETAIL_CONTACTS_LOADER, payload: true });

    let url = `orders/detail/contacts/${payload.id}`;

    let queryPayload = {
      page: payload?.pagination?.page ? payload.pagination.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pagination?.pageSize
        ? payload.pagination.pageSize
        : DEFAULT_PAGINATION.pageSize,
    };

    if (payload?.search?.length) {
      queryPayload.search = payload.search;
    }

    if (payload?.applyFilters) {
      Object.entries(payload.filters).forEach(([key, value]) => {
        if (key === 'deliveredDate') {
          if (value) {
            let date = Object?.entries(value)
              .map(
                ([nestedKey, nestedValue]) =>
                  `${JSON.stringify(nestedKey)}:${JSON.stringify(nestedValue)}`
              )
              .join(', ');
            queryPayload[key] = `{${date}}`;
          }
        } else if (value.length) {
          queryPayload[key] = `${JSON.stringify(value)}`;
        } else if (typeof value === 'boolean') {
          queryPayload[key] = value;
        }
      });
    }

    const response = await request.get(url, queryPayload);
    if (response.status === 200) {
      const combinedData = response.data.data.rows.map((order) => ({
        ...order,
        checked: false,
      }));
      const ordersContacts = {
        rows: combinedData,
        count: response.data.data.count,
        totalRecordsInDB: response.data.data.totalRecordsInDB,
        to: response.data.data.to,
        from: response.data.data.from,
        currentPage: response.data.data.currentPage,
        lastPage: response.data.data.lastPage,
        perPage: response.data.data.perPage,
      };
      dispatch({ type: SET_ORDERS_DETAIL_CONTACTS_DATA, payload: ordersContacts });
      setTimeout(() => {
        dispatch({ type: SET_ORDER_DETAIL_CONTACTS_LOADER, payload: false });
      }, 10);
    }
  } catch (error) {
    dispatch({ type: SET_ORDER_DETAIL_CONTACTS_LOADER, payload: false });
    return error;
  } finally {
    isFetching = false;
  }
};

const fetchOrderOverviewDetails = (payload) => async (dispatch) => {
  const { id, signal } = payload;
  try {
    dispatch({ type: SET_ORDER_DETAIL_LOADER, payload: true });

    const stepperDetails = await request.get(`/orders/${id}`, undefined, signal);
    const overviewDetails = await request.get(`/orders/detail/${id}`, undefined, signal);
    const analyticDetails = await request.get(`/orders/detail/analytics/${id}`, undefined, signal);

    const combinedData = {
      stepper: stepperDetails.data.data,
      overview: overviewDetails.data.data,
      analytics: analyticDetails.data.data,
    };

    dispatch({ type: SET_ORDERS_DETAIL_OVERVIEW_DETAIL, payload: combinedData });
    return { success: true, data: combinedData };
  } catch (error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      return { success: false, aborted: true };
    }
    if (error.response?.status === 422) {
      const errorMessage = error.response?.data?.message || 'Order not found or access denied';
      dispatch(failure(errorMessage));
      return { success: false, status: 422, shouldRedirect: true };
    }
    return error.response;
  } finally {
    dispatch({ type: SET_ORDER_DETAIL_LOADER, payload: false });
  }
};

const fetchOrderCampaignsDetails = (payload) => async (dispatch) => {
  try {
    const orderCampaigns = await request.get(`orders/get-orders-of-same-automation/${payload.id}`);
    dispatch({
      type: SET_ORDERS_CAMPAIGNS_DETAIL_DETAIL,
      payload: {
        orderId: payload.id,
        ...(orderCampaigns?.data?.data ?? {}),
      },
    });
  } catch (error) {
    return error.response;
  }
};

const fetchOrderBatchingDetails = (payload) => async (dispatch) => {
  try {
    const orderBatching = await request.get(`orders/get-orders-by-batching-uuid/${payload.id}`);
    dispatch({
      type: SET_ORDER_BATCHING_DETAIL,
      payload: orderBatching?.data?.data,
    });
    return { success: true };
  } catch (error) {
    return error.response;
  }
};

const searchAndAdvanceChange = (name, value) => (dispatch) => {
  dispatch({ type: CONTACTS_SEARCH, payload: { name, value } });
};

const cancelOrder = (payload) => async () => {
  try {
    const order = await request.post('orders/cancel-order', payload);
    return order;
  } catch (error) {
    return error.response;
  }
};

const payAndProcess = (payload) => async () => {
  try {
    const order = await request.post('orders/pay-and-process', payload);
    return order;
  } catch (error) {
    return error.response;
  }
};

const removeContact = (payload) => async () => {
  try {
    const order = await request.post('orders/remove-contact', payload);
    return order;
  } catch (error) {
    return error.response;
  }
};

const orderBulkActions =
  (payload, details = false) =>
  async () => {
    try {
      const queryPayload = {};

      queryPayload.ids = payload.ids;
      queryPayload.action = payload.action;

      if (details) {
        queryPayload.orderId = payload.orderId;
      }

      if (payload?.search?.length || payload?.applyFilters) {
        queryPayload.filters = {};
      }

      if (payload?.search?.length) {
        queryPayload.filters.search = payload.search;
      }

      if (payload?.applyFilters && payload.filters && typeof payload.filters === 'object') {
        Object.entries(payload.filters).forEach(([key, value]) => {
          if (key === 'selectedTemplates' || key === 'campaigns') return;
          if (
            key === 'createdDate' ||
            key === 'mailingDate' ||
            key === 'mailedDate' ||
            key === 'automationId'
          ) {
            if (value) {
              queryPayload.filters[key] = value;
            }
          } else if (Array.isArray(value) && value.length) {
            queryPayload.filters[key] = value;
          } else if (value != null && !Array.isArray(value)) {
            queryPayload.filters[key] = value;
          }
        });
      }

      const orderBulk = await request.post('orders/bulk-actions', queryPayload);
      return orderBulk;
    } catch (error) {
      return error.response;
    }
  };

export {
  fetchOrders,
  fetchFiltersLabels,
  fetchOrderCost,
  creatOrder,
  fetchOrderContactDetails,
  fetchOrderOverviewDetails,
  searchAndAdvanceChange,
  cancelOrder,
  payAndProcess,
  removeContact,
  orderBulkActions,
  fetchOrderCampaignsDetails,
  fetchOrderBatchingDetails,
};
